<template>
  <div class="h-[500px]">
    <ag-grid-vue
      theme="legacy"
      :rowData="waivers"
      :columnDefs="colDefs"
      :defaultColDef="defaultColDefs"
      :autoSizeStrategy="{ type: 'fitGridWidth' }"
      :singleClickEdit="true"
      :stopEditingWhenCellsLoseFocus="true"
      :suppressDragLeaveHidesColumns="true"
      class="py-3 h-full"
      :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
      :pinnedBottomRowData="[{}]"
      :isFullWidthRow="(params: IsFullWidthRowParams) => params.rowNode.rowPinned === 'bottom'"
      :fullWidthCellRenderer="CustomAddNewCellRenderer"
      :fullWidthCellRendererParams="{
        callback: () => addNewWaiver(),
        label: 'waiver',
        display: canEdit,
      }"
      @grid-size-changed="resize"
      @grid-ready="(event: GridReadyEvent) => gridApi = event.api"
    ></ag-grid-vue>
  </div>
  <div class="flex justify-between">
    <Button
      v-if="permissions.addLienWaiverDescriptions && canEdit"
      label="Edit Descriptions"
      class="w-96 mx-auto"
      @click="editDescriptions = true"
    ></Button>
  </div>
  <TSDialog label="Edit Waiver Descriptions" v-model:visible="editDescriptions" icon="pencil" v-on:after-hide="resize" size="w-[700px]">
    <template #content>
      <div class="mt-3">
        <ListManager :default-code-table="CodeTableNumber.WAIVER_DESCRIPTION" isModal @codes-updated="descriptionsUpdated"></ListManager>
      </div>
    </template>
  </TSDialog>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import CustomDeleteIconCellRenderer from "@/components/cell-renderers/CustomDeleteIconCellRenderer.vue";
import CustomCheckboxRenderer from "@/components/cell-renderers/CustomCheckboxRenderer.vue";
import CustomAddNewCellRenderer from "@/components/cell-renderers/CustomAddNewCellRenderer.vue";
import { usePropsStore } from "@/stores/props";
import type { Waiver } from "@/types/Waiver";
import { dateFormatter, getCellEditableClass, getEntityDisplay, getTableData } from "@/services/Utility";
import type { Code } from "@/types/Codes";
import type { Entity } from "@/types/Entity";
import type { UserPermissions } from "@/types/User";
import { CodeTableNumber } from "@/enums/CodeTable";
import type { GridApi, GridReadyEvent, IsFullWidthRowParams, ValueFormatterParams } from "ag-grid-community";
import DropdownCellRenderer from "../cell-renderers/DropdownCellRenderer.vue";

const waivers = defineModel<Waiver[]>("waivers", { required: true });
const waiverDescriptions = defineModel<Code[]>("waiverDescriptions", { required: true });

const editDescriptions = ref(false);

interface Props {
  userEntity: Entity;
  projectId: string;
  permissions: UserPermissions;
  canEdit: boolean;
}
const props = defineProps<Props>();

const gridApi = ref<GridApi>();
const propsStore = usePropsStore();
const darkVal = computed<boolean>(() => propsStore.darkMode);

const resize = (): void => {
  gridApi.value?.sizeColumnsToFit();
};

const defaultColDefs = {
  floatingFilter: false,
  enableCellChangeFlash: true,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  headerClass: "grid-align-center text-center",
  cellDataType: "text",
};

const descriptionsUpdated = (descriptions: Code[]): void => {
  waiverDescriptions.value = descriptions;
  const waiverDescriptionCol = colDefs.value.find((def) => def.field === "waiverDescription");

  if (waiverDescriptionCol?.cellRendererParams) waiverDescriptionCol.cellRendererParams.options = descriptions;
};

const addNewWaiver = (): void => {
  if (waiverDescriptions.value.length == 0) {
    error("Waiver Descriptions Missing", "Add at least one waiver description before adding a waiver");
    return;
  }

  const newWaiver: Waiver = {
    waiverId: null,
    projectId: props.projectId,
    ordinal: waivers.value.length + 1,
    dateStamp: new Date(),
    waiverProcessor: null,
    approved: false,
    waiverApprover: "",
    waiverDescription: waiverDescriptions.value[0],
  };

  gridApi.value?.applyTransaction({
    add: [newWaiver],
  });

  waivers.value = getTableData(gridApi.value);
};

const deleteWaiver = (waiver: Waiver): void => {
  for (let i = waiver.ordinal; i < waivers.value.length; i++) {
    waivers.value[i].ordinal -= 1;
  }

  gridApi.value?.applyTransaction({
    remove: [waiver],
  });

  waivers.value = getTableData(gridApi.value);
  success("Waiver Deleted", "A waiver was successfully deleted");
};

const changeApproved = (rowIndex: number, isApproved: boolean): void => {
  const rowData = gridApi.value?.getDisplayedRowAtIndex(rowIndex);
  if (rowData == null) return;
  rowData.setDataValue("approved", isApproved);
  if (isApproved) rowData.setDataValue("waiverProcessor", props.userEntity);
  else rowData.setDataValue("waiverProcessor", null);
};

const setDescription = (rowIndex: number, description: Code): void => {
  const rowData = gridApi.value?.getDisplayedRowAtIndex(rowIndex);
  if (rowData == null) return;
  let data = rowData.data;
  data.waiverDescription = description;
  rowData.setData(data);
};

const colDefs = ref([
  {
    field: "delete",
    headerName: "",
    cellClass: "text-center flex items-center justify-center",
    cellRenderer: CustomDeleteIconCellRenderer,
    cellRendererParams: {
      deleteConfirmMessage: (waiver: Waiver) => `Are you sure you want to delete this waiver in row "${waiver.ordinal}"?`,
      deleteConfirmHeader: () => "Delete Waiver",
      callback: deleteWaiver,
      checkDeletable: (waiver: Waiver) => props.canEdit,
    },
    width: 50,
    minWidth: 50,
    hide: !props.permissions.approveLienWaivers || !props.canEdit,
  },
  {
    field: "ordinal",
    headerName: "#",
    width: 75,
    cellClass: "text-center",
  },
  {
    field: "dateStamp",
    cellDataType: "date",
    headerName: "Date",
    width: 100,
    valueFormatter: (params: ValueFormatterParams) => dateFormatter(params),
    cellClass: "text-center",
  },
  {
    field: "waiverDescription",
    headerName: "Description",
    width: 300,
    cellRenderer: DropdownCellRenderer,
    cellRendererParams: {
      options: waiverDescriptions.value,
      optionsLabel: "name",
      setValue: setDescription,
    },
    editable: false,
    cellClass: () => getCellEditableClass(),
  },
  {
    field: "approved",
    headerName: "Approved",
    width: 100,
    cellDataType: "boolean",
    cellRenderer: CustomCheckboxRenderer,
    cellRendererParams: {
      setValue: changeApproved,
      disabled: !props.permissions.approveLienWaivers || !props.canEdit,
    },
    cellClass: () => getCellEditableClass() + " text-center",
  },
  {
    field: "waiverApprover",
    headerName: "Approver",
    width: 300,
    editable: props.canEdit,
    cellClass: () => getCellEditableClass(),
  },
  {
    field: "waiverProcessor",
    headerName: "Processor",
    cellDataType: "object",
    width: 300,
    valueFormatter: (params: ValueFormatterParams) => {
      return getEntityDisplay(params.value);
    },
  },
]);
</script>
